import { BaseBullet } from './Bullet';
import { Vector2 } from './types';

export class PenetratingBullet extends BaseBullet {
  public id: string;
  private penetrationCount: number = 0;
  private maxPenetrations: number = 3;
  
  constructor(x: number, y: number) {
    super(x, y, 4, 10, 1, 500);
    this.velocity.y = -this.speed;
    this.id = `penetrating-bullet-${Date.now()}-${Math.random()}`;
  }

  onHit(): boolean {
    this.penetrationCount++;
    return this.penetrationCount >= this.maxPenetrations;
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.shadowBlur = 10;
    ctx.shadowColor = '#0088ff';
    ctx.fillStyle = '#0088ff';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    // 貫通弾の光る効果
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + 1, this.position.y + 2, 2, 6);
    ctx.restore();
  }
}

export class BombExplosion {
  position: Vector2;
  radius: number = 0;
  maxRadius: number = 150; // 300から150に縮小（半分）
  speed: number = 300; // 500から300に減速
  alive: boolean = true;
  particles: BombParticle[] = [];

  constructor(x: number, y: number) {
    this.position = { x, y };
    
    // 爆発パーティクル生成（数を減らして控えめに）
    for (let i = 0; i < 30; i++) { // 60から30に減少
      const angle = (Math.PI * 2 * i) / 30;
      const speed = 150 + Math.random() * 200; // 速度も控えめに
      this.particles.push(new BombParticle(
        x, y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      ));
    }
  }

  update(deltaTime: number): void {
    this.radius += this.speed * deltaTime;
    this.particles.forEach(particle => particle.update(deltaTime));
    
    if (this.radius >= this.maxRadius) {
      this.alive = false;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    const alpha = Math.max(0, 1 - (this.radius / this.maxRadius));
    
    ctx.save();
    ctx.globalAlpha = alpha * 0.3;
    
    // 爆発の円
    ctx.fillStyle = '#ff4444';
    ctx.beginPath();
    ctx.arc(this.position.x, this.position.y, this.radius, 0, Math.PI * 2);
    ctx.fill();
    
    // 爆発の縁
    ctx.strokeStyle = '#ffff44';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(this.position.x, this.position.y, this.radius, 0, Math.PI * 2);
    ctx.stroke();
    
    ctx.restore();
    
    // パーティクル
    this.particles.forEach(particle => particle.render(ctx));
  }

  isInRange(x: number, y: number): boolean {
    const dx = x - this.position.x;
    const dy = y - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= this.radius;
  }
}

class BombParticle {
  position: Vector2;
  velocity: Vector2;
  color: string;
  size: number;
  life: number = 1.0;
  decay: number = 2.0;

  constructor(x: number, y: number, vx: number, vy: number) {
    this.position = { x, y };
    this.velocity = { x: vx, y: vy };
    this.color = ['#ff4444', '#ff8844', '#ffff44', '#ffffff'][Math.floor(Math.random() * 4)];
    this.size = 3 + Math.random() * 4;
  }

  update(deltaTime: number): void {
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    this.velocity.x *= 0.95;
    this.velocity.y *= 0.95;
    this.life -= this.decay * deltaTime;
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (this.life <= 0) return;
    
    ctx.save();
    ctx.globalAlpha = this.life;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.position.x, this.position.y, this.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

export class ShieldEffect {
  position: Vector2;
  radius: number = 40;
  rotation: number = 0;
  pulseTimer: number = 0;
  
  constructor(x: number, y: number) {
    this.position = { x, y };
  }

  update(deltaTime: number, playerX: number, playerY: number): void {
    this.position.x = playerX;
    this.position.y = playerY;
    this.rotation += deltaTime * 2;
    this.pulseTimer += deltaTime * 3;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const pulse = 0.7 + 0.3 * Math.sin(this.pulseTimer);
    
    ctx.save();
    ctx.translate(this.position.x, this.position.y);
    ctx.rotate(this.rotation);
    ctx.globalAlpha = pulse * 0.6;
    
    // シールドの六角形
    ctx.strokeStyle = '#ffff00';
    ctx.lineWidth = 3;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const x = Math.cos(angle) * this.radius;
      const y = Math.sin(angle) * this.radius;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    ctx.stroke();
    
    // 内側の光
    ctx.fillStyle = '#ffff00';
    ctx.globalAlpha = pulse * 0.2;
    ctx.fill();
    
    ctx.restore();
  }
}